import { useState, useEffect } from 'react';
import styled from 'styled-components';
import xor from 'lodash.xor';

import RadioButton from '/components/ui/RadioButton';
import NumberInput, { NumberInputProps } from '/components/ui/NumberInput';
import NumberSlider, { NumberSliderProps } from '/components/ui/NumberSlider';
import Button from '/components/ui/buttons/Button';
import FileInput from '/components/ui/FileInput';
import { colors } from '/css';
import imgixUrl from '/lib/utils/imgixUrl';
import box from '/lib/utils/box';
import ChangeLock from './ChangeLock';
import QuestionHeader from './QuestionHeader';
import {
    JobTemplate,
    Question,
    Choice,
    Answer,
    QuestionType,
} from '/types/jobTemplateTypes';
import Image from '/types/imageType';
import useProductModal from '/hooks/useProductModal';

interface Props {
    questions: Question[];
    answers: Answer[];
    materialsEdited: boolean;
    onAnswerChange: (questionId: string, answer: Answer) => void;
    hideProductSelector?: boolean;
    showSelectedProductInProductSelector?: boolean;
}

export default function JobQuestions({
    questions,
    answers,
    onAnswerChange,
    materialsEdited,
    hideProductSelector,
    showSelectedProductInProductSelector,
}: Props) {
    const [editLock, setEditLock] = useState(materialsEdited);

    useEffect(() => {
        if (materialsEdited && !editLock) {
            setEditLock(true);
        }
    }, [materialsEdited]);

    return (
        <QuestionList
            id="job-questions"
            questions={questions}
            answers={answers}
            onAnswerChange={onAnswerChange}
            editLock={editLock}
            setEditLock={setEditLock}
            listKey="job-questions"
            hideProductSelector={hideProductSelector}
            showSelectedProductInProductSelector={
                showSelectedProductInProductSelector
            }
        />
    );
}

interface NumberQuestionProps extends NumberInputProps, NumberSliderProps {
    theme?: 'slider' | 'ticker';
}

const NumberQuestion = ({ theme, ...props }: NumberQuestionProps) => {
    return theme === 'slider' ? (
        <NumberSlider {...props} />
    ) : (
        <NumberInput {...props} />
    );
};

interface QuestionListProps extends React.HTMLAttributes<HTMLUListElement> {
    questions: JobTemplate['jobTypeVersion']['questions'];
    answers: JobTemplate['answers'];
    onAnswerChange: (questionId: string, answer: Answer) => void;
    editLock?: boolean;
    setEditLock?: (val: boolean) => void;
    listKey?: string;
    hideProductSelector?: boolean;
    showSelectedProductInProductSelector?: boolean;
}

export const QuestionList = ({
    questions,
    answers,
    onAnswerChange,
    editLock = false,
    setEditLock,
    listKey = '',
    hideProductSelector,
    showSelectedProductInProductSelector,
    ...rest
}: QuestionListProps) => {
    const { openProductModal, productModalRender } = useProductModal();
    const [hoveredQuestionId, setHoveredQuestionId] = useState<string>('');
    return (
        <ul style={{ position: 'relative' }} {...rest}>
            {questions.map((question, questionIndex) => {
                if (!question.visible) return null;

                const {
                    id,
                    type,
                    choices,
                    label,
                    defaultValue,
                    theme,
                    minValue,
                    maxValue,
                    fileLimit,
                } = question;

                if (
                    [QuestionType.RADIO, QuestionType.CHECKBOX].includes(
                        type
                    ) ||
                    (type === QuestionType.PRODUCT_SELECTOR &&
                        (!hideProductSelector ||
                            showSelectedProductInProductSelector))
                ) {
                    let questionChoices;

                    if (
                        type === QuestionType.PRODUCT_SELECTOR &&
                        showSelectedProductInProductSelector
                    ) {
                        const answer = answers.find(
                            (a) => a.questionId === question.id
                        );

                        questionChoices = choices.filter((choice) => {
                            if (answer?.answerId.includes(choice.id)) {
                                return true;
                            }
                        });
                    } else {
                        questionChoices = choices;
                    }

                    return (
                        <JobQuestion
                            key={`${listKey}-${questionIndex}-${id}`}
                            className={`type-${type} mb-60 ${
                                !question.visible ? 'hidden' : ''
                            }`}
                            onMouseEnter={() => setHoveredQuestionId(id)}
                            onMouseLeave={() => setHoveredQuestionId('')}
                        >
                            <QuestionHeader question={question} />
                            <ul className="options">
                                <Choices
                                    listKey={listKey}
                                    choices={questionChoices}
                                    answers={answers}
                                    question={question}
                                    questionIndex={questionIndex}
                                    questionHidden={!question.visible}
                                    type={type}
                                    onAnswerChange={onAnswerChange}
                                    setProductPreview={(productId) =>
                                        openProductModal(productId!)
                                    }
                                />
                            </ul>
                            {editLock && (
                                <ChangeLock
                                    locked={hoveredQuestionId === id}
                                    onDisableLock={() =>
                                        setEditLock
                                            ? setEditLock(false)
                                            : undefined
                                    }
                                />
                            )}
                        </JobQuestion>
                    );
                }

                if (type === QuestionType.NUMBER) {
                    const min = typeof minValue === 'undefined' ? 1 : minValue;
                    const answerObject = answers.find(
                        (a) => a.questionId === id
                    );
                    let answer = answerObject
                        ? Number(answerObject.answerValue)
                        : Number(defaultValue);
                    if (!answer && answer !== 0) answer = Number(defaultValue);
                    if (answer < min) answer = min;
                    return (
                        <JobQuestion
                            key={`${listKey}-${questionIndex}-${id}`}
                            className={`type-${type} mb-60`}
                            onMouseEnter={() => setHoveredQuestionId(id)}
                            onMouseLeave={() => setHoveredQuestionId('')}
                        >
                            <QuestionHeader question={question} />
                            <NumberQuestion
                                theme={theme}
                                name={`question-${questionIndex}`}
                                label={label}
                                minValue={min}
                                maxValue={maxValue || 100}
                                value={answer}
                                onChange={(value: number) =>
                                    onAnswerChange(id, {
                                        questionId: id,
                                        questionType: QuestionType.NUMBER,
                                        answerId: '',
                                        answerValue: value,
                                    })
                                }
                            />
                            {editLock && (
                                <ChangeLock
                                    locked={hoveredQuestionId === id}
                                    onDisableLock={() =>
                                        setEditLock
                                            ? setEditLock(false)
                                            : undefined
                                    }
                                />
                            )}
                        </JobQuestion>
                    );
                }

                if (type === QuestionType.FILE_UPLOAD) {
                    return (
                        <JobQuestion
                            key={`${listKey}-${questionIndex}-${id}`}
                            className={`type-${type.replace(' ', '-')} mb-60`}
                        >
                            <QuestionHeader question={question} />

                            <FileInput
                                id={`file-upload-${id}`}
                                lightbox
                                customerUpload
                                maxFiles={fileLimit || 5}
                                files={
                                    answers.find((a) => a.questionId === id)
                                        ?.answerValue || []
                                }
                                onUpload={(files: Image[]) =>
                                    onAnswerChange(id, {
                                        questionId: id,
                                        questionType: QuestionType.FILE_UPLOAD,
                                        answerId: '',
                                        answerValue: files,
                                    })
                                }
                            />
                        </JobQuestion>
                    );
                }

                return null;
            })}

            {productModalRender}
        </ul>
    );
};

interface ChoicesProps {
    listKey?: string;
    choices: Choice[];
    answers: Answer[];
    question: Question;
    questionIndex: number;
    questionHidden: boolean;
    type: QuestionType;
    onAnswerChange: (questionId: string, answer: Answer) => void;
    setProductPreview: (val?: number) => void;
}

const Choices = ({
    listKey = '',
    choices,
    answers,
    question,
    questionIndex,
    questionHidden,
    type,
    onAnswerChange,
    setProductPreview,
}: ChoicesProps) => {
    const [showingAllChoices, setShowingAllChoices] = useState(
        !question.answerLimit ||
            (question.answerLimit && choices.length <= question.answerLimit)
    );

    // expand choices is selected answer is hidden (based on query answers)
    useEffect(() => {
        const answer = answers.find((a) => a.questionId === question.id);

        if (!answer) return;

        const lastAnswerIndex = choices
            .filter(noDisabled)
            .map((c) => (answer.answerId.includes(c.id) ? true : false))
            .lastIndexOf(true);

        if (
            !showingAllChoices &&
            question.answerLimit &&
            lastAnswerIndex + 1 > question.answerLimit
        ) {
            setShowingAllChoices(true);
        }
    }, [answers]);

    const noDisabled = (choice: Choice) => !choice.disabled;

    const choiceLimit = (c: Choice, i: number) =>
        showingAllChoices || !question.answerLimit
            ? true
            : i < question.answerLimit;

    return (
        <>
            {choices
                .filter(noDisabled)
                .filter(choiceLimit)
                .map((choice, choiceIndex) => {
                    const answer = answers.find(
                        (a) => a.questionId === question.id
                    );

                    const choiceKey = `${listKey}-${questionIndex}-${question.id}-${choiceIndex}`;

                    const isChecked = (choice: Choice) => {
                        if (type === QuestionType.RADIO)
                            return !questionHidden
                                ? choice.value === answer?.answerValue
                                : choice.value === question.defaultValue;
                        if (type === QuestionType.CHECKBOX)
                            return !questionHidden
                                ? box(answer?.answerValue).includes(
                                      choice.value
                                  )
                                : box(question.defaultValue).includes(
                                      choice.value
                                  );
                        if (type === QuestionType.PRODUCT_SELECTOR) {
                            return choice.id === answer?.answerId;
                        }
                    };

                    const onChoiceClick = (value: string) => {
                        if (
                            [
                                QuestionType.RADIO,
                                QuestionType.PRODUCT_SELECTOR,
                            ].includes(type)
                        ) {
                            const maybeProductId = choice.productPage
                                ? { productId: choice.productPage }
                                : {};

                            onAnswerChange(question.id, {
                                questionId: question.id,
                                questionType: type,
                                answerId: choice.id,
                                answerValue: value,
                                ...maybeProductId,
                            });
                        }
                        if (type === QuestionType.CHECKBOX) {
                            const productIdsArray = xor(
                                (answer?.productId as number[]) || [],
                                [choice.productPage]
                            ).filter((id) => id) as number[];
                            const maybeProductId = productIdsArray.length
                                ? { productId: productIdsArray }
                                : {};

                            onAnswerChange(question.id, {
                                questionId: question.id,
                                questionType: type,
                                answerId: xor(answer?.answerId || [], [
                                    choice.id,
                                ]),
                                answerValue: xor(
                                    (answer?.answerValue as string[]) || [],
                                    [choice.value]
                                ),
                                ...maybeProductId,
                            });
                        }
                    };

                    return (
                        <li key={choiceKey}>
                            <RadioButton
                                name={choiceKey}
                                type={
                                    type === QuestionType.CHECKBOX
                                        ? QuestionType.CHECKBOX
                                        : QuestionType.RADIO
                                }
                                value={choice.value}
                                label={choice.label}
                                description={
                                    <>
                                        {choice.description}
                                        {choice.disabled && (
                                            <div
                                                style={{ color: colors.orange }}
                                            >
                                                Choice disabled
                                            </div>
                                        )}
                                    </>
                                }
                                theme={
                                    choice.productPage ? 'product' : undefined
                                }
                                onChange={onChoiceClick}
                                checked={!!isChecked(choice)}
                                image={imgixUrl(choice.imageUrl, {
                                    width: 275,
                                })}
                                imageAlt={choice.imageAlt}
                                onReadMore={() =>
                                    setProductPreview(choice.productPage)
                                }
                                style={{ minHeight: '60px' }}
                            />
                        </li>
                    );
                })}
            {!showingAllChoices &&
                question.answerLimit &&
                choices.length > question.answerLimit && (
                    <Button
                        variant="tertiary"
                        fullWidth
                        onClick={() => setShowingAllChoices(!showingAllChoices)}
                    >
                        {showingAllChoices
                            ? 'vis mindre'
                            : 'Vis flere produkter'}
                    </Button>
                )}
        </>
    );
};

export const JobQuestion = styled.li`
    margin-bottom: 60px;
    position: relative;
    &.type-radio,
    &.type-checkbox {
        margin-bottom: 30px;
    }
    &.type-file-upload {
        z-index: 50;
    }
    &.hidden {
        opacity: 0.2;
        pointer-events: none;
    }
    &.hidden-editable {
        .help-icon,
        .question-title,
        .question-description,
        .number-input,
        .number-slider,
        .radio-button,
        .file-upload {
            opacity: 0.5;
        }
        .number-input,
        .number-slider,
        .radio-button,
        .file-upload {
            pointer-events: none;
        }
    }
    > .question-header {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
    }
    > ul {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        > li {
            width: calc(50% - 12px);
            margin-bottom: 24px;
            position: relative;
            @media (max-width: 640px) {
                width: 100%;
                max-width: 380px;
            }
            .radio-button {
                width: 100%;
                display: flex;
            }
        }
    }
    .number-input {
        @media (max-width: 480px) {
            .counter {
                width: 100%;
                margin-right: 0;
                justify-content: space-between;
            }
        }
    }
`;
